/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 11 de noviembre 2024
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Fade,
  InputAdornment,
  keyframes,
  LinearProgress
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { 
  ArrowBack, 
  LockReset, 
  Lock,
  CheckCircle,
  Visibility,
  VisibilityOff
} from "@mui/icons-material";
import api from "../../../axiosConfig";
import logo from '../../../Assets/img/logo.webp';

// Animaciones
const floatAnimation = keyframes`
  0%, 100% { 
    transform: translateY(0px);
  }
  50% { 
    transform: translateY(-10px);
  }
`;

const scanAnimation = keyframes`
  0% { left: -100%; }
  100% { left: 100%; }
`;

const successPulse = keyframes`
  0%, 100% { 
    transform: scale(1);
    opacity: 1;
  }
  50% { 
    transform: scale(1.05);
    opacity: 0.9;
  }
`;

const loadingPulse = keyframes`
  0%, 100% { 
    opacity: 0.6;
  }
  50% { 
    opacity: 1;
  }
`;

// Contenedor principal con fondo degradado
const ResetPasswordContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  background: 'linear-gradient(135deg, #0a2e5c 0%, #051b38 100%)',
  position: 'relative',
  overflow: 'hidden',
  padding: theme.spacing(2),
  backgroundImage: `
    radial-gradient(circle at 20% 80%, rgba(10, 46, 92, 0.4) 0%, transparent 60%),
    radial-gradient(circle at 80% 20%, rgba(10, 46, 92, 0.25) 0%, transparent 60%),
    radial-gradient(circle at 60% 60%, rgba(10, 46, 92, 0.18) 0%, transparent 50%)
  `,
}));

// Logo con efectos
const StyledLogo = styled('img')(({ theme, isMobile }) => ({
  width: isMobile ? 150 : 200,
  marginBottom: theme.spacing(3),
  filter: 'drop-shadow(0 0 20px rgba(10, 46, 92, 0.8))',
  animation: `${floatAnimation} 6s ease-in-out infinite`,
  [theme.breakpoints.down('sm')]: {
    width: 120,
    marginBottom: theme.spacing(2),
  },
}));

// Contenedor del formulario con glass effect
const FormContainer = styled(Box)(({ theme, isMobile }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  padding: isMobile ? theme.spacing(3) : theme.spacing(4),
  borderRadius: 20,
  boxShadow: `
    0 20px 40px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.8),
    0 0 30px rgba(10, 46, 92, 0.2)
  `,
  position: 'relative',
  overflow: 'hidden',
  maxWidth: isMobile ? '90vw' : 400,
  width: '100%',
  
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(10, 46, 92, 0.1), transparent)',
    animation: `${scanAnimation} 8s linear infinite`,
    zIndex: 0,
    pointerEvents: 'none',
  },
}));

// Input personalizado
const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 12,
    transition: 'all 0.3s ease',
    position: 'relative',
    zIndex: 10,
    
    '& fieldset': {
      borderColor: 'rgba(10, 46, 92, 0.3)',
      borderWidth: 2,
    },
    
    '&:hover fieldset': {
      borderColor: 'rgba(10, 46, 92, 0.5)',
    },
    
    '&.Mui-focused fieldset': {
      borderColor: '#0a2e5c',
      boxShadow: '0 0 20px rgba(10, 46, 92, 0.4)',
    },
    
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
    },
  },
  
  '& .MuiInputLabel-root': {
    color: '#2c3e50',
    fontWeight: 600,
    zIndex: 15,
    
    '&.Mui-focused': {
      color: '#0a2e5c',
      fontWeight: 700,
    },
    
    '&.MuiFormLabel-filled': {
      color: '#2c3e50',
    },
  },
  
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    color: '#0a2e5c',
    fontSize: '1.4rem',
    filter: 'drop-shadow(0 0 3px rgba(10, 46, 92, 0.5))',
    transition: 'all 0.3s ease',
  },
  
  '& .MuiOutlinedInput-root.Mui-focused .MuiInputAdornment-root .MuiSvgIcon-root': {
    transform: 'scale(1.1)',
    filter: 'drop-shadow(0 0 5px rgba(10, 46, 92, 0.6))',
  },
}));

// Botón principal
const StyledButton = styled(Button)(({ theme, isMobile, variant }) => ({
  width: '100%',
  padding: isMobile ? theme.spacing(1.5) : theme.spacing(2),
  marginTop: theme.spacing(2),
  borderRadius: 12,
  fontSize: isMobile ? '0.9rem' : '1rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 1,
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  zIndex: 10,
  
  ...(variant === 'primary' ? {
    background: 'linear-gradient(135deg, #0a2e5c, #051b38)',
    color: 'white',
    boxShadow: '0 8px 20px rgba(10, 46, 92, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
    
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
      transition: 'left 0.5s ease',
    },
    
    '&:hover::before': {
      left: '100%',
    },
    
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 25px rgba(10, 46, 92, 0.8), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
      background: 'linear-gradient(135deg, #051b38, #082340)',
    },
    
    '&:disabled': {
      background: 'rgba(10, 46, 92, 0.5)',
      transform: 'none',
      '&::before': {
        display: 'none',
      },
    },
  } : {
    border: '2px solid rgba(10, 46, 92, 0.3)',
    color: '#0a2e5c',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    
    '&:hover': {
      backgroundColor: 'rgba(10, 46, 92, 0.05)',
      borderColor: '#0a2e5c',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 15px rgba(10, 46, 92, 0.2)',
    },
  }),
  
  '&:active': {
    transform: 'translateY(0)',
  },
}));

// Título con iconos
const StyledTitle = styled(Typography)(({ theme, isMobile }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(3),
  color: '#0a2e5c',
  fontSize: isMobile ? '1.3rem' : '1.6rem',
  fontWeight: 700,
  position: 'relative',
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
}));

// Indicador de tiempo
const TimeIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1),
  borderRadius: 8,
  background: 'rgba(255, 193, 7, 0.1)',
  border: '1px solid rgba(255, 193, 7, 0.3)',
  position: 'relative',
  zIndex: 10,
  
  '& .time-text': {
    color: '#f57c00',
    fontSize: '0.85rem',
    fontWeight: 600,
  },
}));

// Alerta de éxito personalizada
const SuccessContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  borderRadius: 16,
  background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(67, 160, 71, 0.05))',
  border: '2px solid rgba(76, 175, 80, 0.3)',
  position: 'relative',
  zIndex: 10,
  animation: `${successPulse} 2s ease-in-out infinite`,
  
  '& .success-icon': {
    fontSize: '3rem',
    color: '#4caf50',
    marginBottom: theme.spacing(2),
    filter: 'drop-shadow(0 0 10px rgba(76, 175, 80, 0.4))',
  },
  
  '& .success-title': {
    color: '#2e7d32',
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  
  '& .success-message': {
    color: '#388e3c',
    fontSize: '0.9rem',
    lineHeight: 1.5,
  },
}));

// Contenedor de carga
const LoadingContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4),
  position: 'relative',
  zIndex: 10,
  
  '& .loading-text': {
    color: '#0a2e5c',
    fontSize: '1rem',
    fontWeight: 600,
    marginTop: theme.spacing(2),
    animation: `${loadingPulse} 2s ease-in-out infinite`,
  },
}));

function ResetPasswordPage() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [token, setToken] = useState("");
  const [expiresIn, setExpiresIn] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get("token");
    setToken(tokenParam || "");

    if (tokenParam) {
      verifyToken(tokenParam);
    } else {
      setError("Token no encontrado en la URL");
    }
  }, [location]);

  const verifyToken = async (token) => {
    try {
      const response = await api.get(
        `/conductor/verify-reset-token?token=${token}`
      );
      if (response.data.valid) {
        setTokenValid(true);
        setExpiresIn(response.data.expiresIn);
      } else {
        setError("El token es inválido o ha expirado");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error al verificar el token");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setError("Las contraseñas no coinciden");
      return;
    }

    if (newPassword.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await api.post("/conductor/reset-password", { token, newPassword });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(
        err.response?.data?.message || "Error al restablecer la contraseña"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  const getPasswordStrength = (password) => {
    if (password.length === 0) return { strength: 0, text: '', color: '' };
    if (password.length < 8) return { strength: 25, text: 'Muy débil', color: '#f44336' };
    if (password.length < 12) return { strength: 50, text: 'Débil', color: '#ff9800' };
    if (password.length < 16) return { strength: 75, text: 'Buena', color: '#2196f3' };
    return { strength: 100, text: 'Muy fuerte', color: '#4caf50' };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  return (
    <ResetPasswordContainer>
      <StyledLogo 
        src={logo} 
        alt="Mogotax Logo" 
        isMobile={isMobile}
      />
      
      <FormContainer isMobile={isMobile}>
        <StyledButton
          variant="secondary"
          startIcon={<ArrowBack />}
          onClick={handleBackToLogin}
          isMobile={isMobile}
          sx={{ mb: 3, textTransform: 'none', justifyContent: 'flex-start' }}
        >
          Volver al inicio
        </StyledButton>
        
        <StyledTitle variant="h4" isMobile={isMobile}>
          <LockReset sx={{ fontSize: isMobile ? '1.3rem' : '1.6rem' }} />
          Restablecer Contraseña
        </StyledTitle>

        {error ? (
          <Fade in={Boolean(error)}>
            <Alert 
              severity="error"
              sx={{ 
                mb: 2,
                borderRadius: 2,
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                border: '1px solid rgba(244, 67, 54, 0.3)',
                position: 'relative',
                zIndex: 10,
              }}
            >
              {error}
            </Alert>
          </Fade>
        ) : success ? (
          <SuccessContainer>
            <CheckCircle className="success-icon" />
            <Typography variant="h6" className="success-title">
              ¡Contraseña actualizada exitosamente!
            </Typography>
            <Typography className="success-message">
              Tu contraseña ha sido restablecida correctamente. 
              Serás redirigido al inicio de sesión en unos segundos.
            </Typography>
            <LinearProgress 
              sx={{ 
                mt: 2, 
                borderRadius: 2,
                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#4caf50'
                }
              }} 
            />
          </SuccessContainer>
        ) : tokenValid ? (
          <>
            {expiresIn && (
              <TimeIndicator>
                <Typography className="time-text">
                  ⏰ Este enlace expira en {Math.round(expiresIn)} minutos
                </Typography>
              </TimeIndicator>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ position: 'relative', zIndex: 10 }}>
              <StyledTextField
                fullWidth
                label="Nueva contraseña"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (error) setError("");
                }}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        sx={{ 
                          minWidth: 'auto',
                          p: 0.5,
                          color: '#0a2e5c',
                          '&:hover': {
                            backgroundColor: 'rgba(10, 46, 92, 0.1)',
                          },
                        }}
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />

              {newPassword.length > 0 && (
                <Box sx={{ mb: 2, position: 'relative', zIndex: 10 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={passwordStrength.strength}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: passwordStrength.color,
                        borderRadius: 4,
                      }
                    }}
                  />
                  <Typography sx={{ 
                    fontSize: '0.75rem', 
                    color: passwordStrength.color, 
                    fontWeight: 600,
                    mt: 0.5 
                  }}>
                    Seguridad: {passwordStrength.text}
                  </Typography>
                </Box>
              )}

              <StyledTextField
                fullWidth
                label="Confirmar nueva contraseña"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (error) setError("");
                }}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        sx={{ 
                          minWidth: 'auto',
                          p: 0.5,
                          color: '#0a2e5c',
                          '&:hover': {
                            backgroundColor: 'rgba(10, 46, 92, 0.1)',
                          },
                        }}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />

              {confirmPassword.length > 0 && confirmPassword !== newPassword && (
                <Alert 
                  severity="warning" 
                  sx={{ 
                    mb: 2,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    border: '1px solid rgba(255, 152, 0, 0.3)',
                    position: 'relative',
                    zIndex: 10,
                  }}
                >
                  Las contraseñas no coinciden
                </Alert>
              )}

              <StyledButton
                type="submit"
                variant="primary"
                disabled={
                  loading ||
                  newPassword.length < 8 ||
                  confirmPassword !== newPassword
                }
                isMobile={isMobile}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    Actualizando contraseña...
                  </Box>
                ) : (
                  'Restablecer contraseña'
                )}
              </StyledButton>
            </Box>
          </>
        ) : (
          <LoadingContainer>
            <CircularProgress size={60} sx={{ color: '#0a2e5c' }} />
            <Typography className="loading-text">
              Verificando enlace de recuperación...
            </Typography>
          </LoadingContainer>
        )}
      </FormContainer>
    </ResetPasswordContainer>
  );
}

export default ResetPasswordPage;
